import './item-card.scss';

import { Slot } from '@radix-ui/react-slot';
import type { PropsWithChildren, ReactNode } from 'react';

import { Skeleton } from '../../atoms/skeleton/skeleton.tsx';
import { cn } from '../../utils/helpers.ts';

export type ItemCardProps = PropsWithChildren & {
  asChild?: boolean;
  className?: string;
  color?: 'negative' | 'warning';
};

/**
 * Item Card
 */
export const ItemCard: React.FC<ItemCardProps> = ({
  asChild = false,
  color,
  className,
  children,
}) => {
  const Comp = asChild ? Slot : 'div';
  const colorClass = color
    ? {
        warning: 'ItemCard--warning',
        negative: 'ItemCard--negative',
      }[color]
    : '';

  return (
    <Comp className={cn(['ItemCard rounded-md', colorClass, className])}>
      {children}
    </Comp>
  );
};

export const ItemCardSection: React.FC<
  PropsWithChildren<{ staticPadding?: boolean }>
> = ({ staticPadding = false, children }) => (
  <div
    className={cn('ItemCard__section w-full p-comfy-sm', {
      'md:p-comfy-lg': !staticPadding,
    })}
  >
    {children}
  </div>
);

/**
 * Item Card Header
 *
 * Slots:
 * - `titleBadge`: rendered inline next to `title` on the left.
 * - `badge`: rendered on the right side of the row (existing behavior).
 * - `action`: rendered on the right side after `badge`, for trailing
 *   controls like a menu button.
 */
export const ItemCardHeader: React.FC<{
  title?: string;
  className?: string;
  titleBadge?: ReactNode;
  badge?: ReactNode;
  action?: ReactNode;
  isTitleLarge?: boolean;
}> = ({
  title,
  className = '',
  titleBadge,
  badge,
  action,
  isTitleLarge = false,
}) => (
  <div
    className={cn(
      [
        'ItemCard__header flex justify-between items-start md:items-center',
        className,
      ],
      {
        'mb-comfy-sm md:mb-comfy-md': !badge && !titleBadge,
        'mb-comfy-xs md:mb-comfy-sm': !!badge || !!titleBadge,
      },
    )}
  >
    <div className="flex flex-col items-start gap-snug-xl md:flex-row md:items-center md:gap-snug-xl min-w-0">
      {title && (
        <h3
          className={cn(
            'label-neutral-amplified label-potent md:label-potent',
            {
              'label-md md:label-lg': !isTitleLarge,
              'label-xl': isTitleLarge,
            },
          )}
        >
          {title}
        </h3>
      )}
      {titleBadge}
    </div>
    {(badge || action) && (
      <div className="flex items-center gap-snug-xl shrink-0">
        {badge}
        {action}
      </div>
    )}
  </div>
);

/**
 * Item Card Body - if you need full control
 */
export const ItemCardBody: React.FC<
  PropsWithChildren<{ className?: string }>
> = ({ children, className }) => (
  <div
    className={cn([
      'ItemCard__body grid gap-comfy-xs md:gap-comfy-sm items-center',
      className,
    ])}
  >
    {children}
  </div>
);

export const ItemCardSummary: React.FC<{
  icon?: ReactNode;
  title?: string;
  subTitle?: string;
  isWarning?: boolean;
  isError?: boolean;
  isSubtleTitle?: boolean;
}> = ({
  icon,
  title,
  subTitle,
  isWarning = false,
  isError = false,
  isSubtleTitle = false,
}) => {
  if (!title && !subTitle) {
    return;
  }

  return (
    <>
      {icon}
      {(title || subTitle) && (
        <div className="ItemCard__titlegroup">
          {title && (
            <div
              className={cn('ItemCard__title label-md md:label-lg mb-snug-md', {
                'label-potent md:label-potent label-neutral-amplified':
                  !isSubtleTitle,
                'label-neutral-potent': isSubtleTitle,
              })}
            >
              {title}
            </div>
          )}

          {subTitle && (
            <div
              className={cn(
                `ItemCard__subtitle label-neutral-mild label-sm md:label-md`,
                {
                  'label-warning-moderate': isWarning,
                  'label-negative-moderate': isError,
                },
              )}
            >
              {subTitle}
            </div>
          )}
        </div>
      )}
    </>
  );
};

/**
 * Item Card Meta
 *
 * NOTE Organizes right-aligned text with an optional badge.
 */
export const ItemCardMeta: React.FC<
  PropsWithChildren & {
    className?: string;
    text?: string;
    textContrast?: string;
    badge?: ReactNode;
  }
> = ({ className, text, textContrast, badge, children }) => (
  <div
    className={cn(
      'flex flex-col gap-snug-md md:gap-snug-xl items-end text-right',
      className,
    )}
  >
    {(text || textContrast) && (
      <span className="label-md md:label-lg">
        <span className="label-neutral-amplified">{text}</span>
        {textContrast && (
          <span className="label-neutral-mild">{textContrast}</span>
        )}
      </span>
    )}
    {badge}
    {children}
  </div>
);

export const ItemCardDivider = () => <div className="ItemCard__divider" />;

/**
 * Item Card Footer
 *
 * NOTE that this should almost always take in one or more client components.
 */
export const ItemCardFooter: React.FC<
  PropsWithChildren<{ justify?: 'end' | 'between' }>
> = ({ children, justify = 'between' }) => (
  <ItemCardSection>
    <div
      className={cn([
        'ItemCard__footer flex',
        {
          'justify-between': justify === 'between',
          'justify-end': justify === 'end',
        },
      ])}
    >
      {children}
    </div>
  </ItemCardSection>
);

export const ItemCardSkeleton = () => <Skeleton height={180} />;
